/* istanbul ignore file */

import React from 'react';
import { Switch, Route, useLocation } from '@react-router';
import { JoyKitThemeProvider, createGlobalStyle, Box, GridOverlay, NoSsr } from '@withjoy/joykit';

import '@assets/charm.css';
import { TelemetryProvider } from './GuestSite.telemetry';

import { withWindow } from '@shared/utils/withWindow';
import { LazyHotelBookingApp } from './routes/HotelBooking/HotelBooking.lazy';
import { MediaCollection } from './routes/MediaCollection';
import { StandAloneAlohaMenu } from './packages/layout-engine/components/StandAloneAlohaMenu';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { HotelSearchContextParams, HotelSearchSource } from '@shared/constants/accommodations';
import { useQueryParamHelper } from '@shared/core/queryString';
import { EventHandleFetcher } from './components/EventHandleFetcher';
import { UUID_ROUTE_PATTERN } from '@shared/utils/uuid';

import { LazyGuestSite } from './routes/GuestSite/GuestSite.lazy';
import { LazyRegistrationGuestWithHead } from './routes/registration/RegistrationGuestWithHead.lazy';
import { LazyEventAccommodations } from '@apps/stays/EventAccommodations/EventAccommodations.lazy';
import { LazyOtaHotelSearch } from './routes/HotelBooking/routes/HotelSearch/OtaHotelSearch.lazy';
import { LazyGuestHotelSearch } from './routes/HotelBooking/routes/HotelSearch/GuestHotelSearch.lazy';
import { LazyGuestCarSearch } from './routes/HotelBooking/routes/CarSearch/GuestCarSearch.lazy';
import { LazyGuestCarDetails } from './routes/HotelBooking/routes/CarDetails/GuestCarDetails.lazy';
import { LazyBookYourStay } from './routes/HotelBooking/routes/BookYourStay/BookYourStay.lazy';
import { LazyHotelBookingPartnerSearch } from './routes/HotelBooking/routes/HotelBookingPartnerSearch/HotelBookingPartnerSearch.lazy';
import { LazyAccommodationPlacePartner } from './routes/HotelBooking/routes/AccommodationPlacePartner/AccommodationPlacePartner.lazy';
import { LazyCollaborators } from './routes/Collaborators/Collaborators.lazy';
import { LazyCashFundGuest } from './routes/CashFunds/routes/CashFundGuest/CashFundGuest.lazy';
import { LazyHotelDetails } from './routes/HotelBooking/routes/HotelDetails/HotelDetails.lazy';
import { LazyTicketPass } from './routes/tickets/pass/TicketPass.lazy';
import { LazyStaysPdpHead } from '@apps/stays/EventAccommodations/components/StaysPdpHead';

const EventAccommodationsWrapper = ({ eventHandle }: { eventHandle: string }) => {
  const isMobile = useIsMobileScreen();
  const [isNewMobilePDPEnabled] = useIsFeatureEnabled('accomodationsNewMobilePDPEnabled', { skip: !isMobile });
  const shouldShowAlohaMenu = !isNewMobilePDPEnabled;

  return (
    <>
      <LazyEventAccommodations eventHandle={eventHandle} />
      {shouldShowAlohaMenu && <StandAloneAlohaMenu eventHandle={eventHandle} />}
    </>
  );
};

const GlobalStyle = createGlobalStyle`
  body {
    font-family: 'Gotham SSm';
  }
`;

export interface GuestProps {}

const HotelSearchRoute = ({ eventHandle }: { eventHandle: string }) => {
  const queryParamHelper = useQueryParamHelper();
  const source = queryParamHelper.getValueString(HotelSearchContextParams.source);
  return source === HotelSearchSource.GUEST_MAP ? <LazyGuestHotelSearch eventHandle={eventHandle} /> : <LazyOtaHotelSearch eventHandle={eventHandle} />;
};

const StaysPdpRoute = ({ joyPlaceId }: { joyPlaceId: string }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const eventId = searchParams.get('eventId');

  return (
    <>
      <LazyStaysPdpHead
        joyPlaceId={joyPlaceId}
        eventId={eventId}
      />
      <EventHandleFetcher>{eventHandle => <EventAccommodationsWrapper eventHandle={eventHandle} />}</EventHandleFetcher>
    </>
  );
};

const Guest: React.FC<GuestProps> = () => {
  return (
    <JoyKitThemeProvider>
      <GlobalStyle />
      <Box
        as="main"
        overflowX={['clip', null, 'initial']}
      >
        <NoSsr>
          <GridOverlay />
        </NoSsr>
        <Switch>
          {/*
            Standalone stays PDP: /stays/:joyPlaceId?eventId=...
            StaysPdpHead renders OG meta tags during SSR so link-preview crawlers
            (Slack, iMessage, WhatsApp) can display a rich unfurl.
            The PDP body stays client-only via EventHandleFetcher (which uses NoSsr internally).
          */}
          <Route
            path={`/stays/:joyPlaceId(${UUID_ROUTE_PATTERN})`}
            render={({ match }) => {
              return <StaysPdpRoute joyPlaceId={match.params.joyPlaceId} />;
            }}
          />
          <Route
            path="/:eventHandle/moments"
            render={() => {
              // moments is an app-browser page, need to reload to let charm-server 307
              withWindow(() => window.location.reload());
              return null;
            }}
          />
          <Route
            path="/:eventHandle/accommodations/:accommodationId"
            render={({ match }) => {
              return <EventAccommodationsWrapper eventHandle={match.params.eventHandle} />;
            }}
          />
          <Route
            path="/:eventHandle/hotels/:joyPlaceId"
            render={({ match }) => {
              return <EventAccommodationsWrapper eventHandle={match.params.eventHandle} />;
            }}
          />
          <Route
            path="/:eventHandle/accommodation-place/:accommodationPlaceId?"
            render={({ match }) => {
              return <LazyAccommodationPlacePartner accommodationPlaceV2ByIdId={match.params.accommodationPlaceId} />;
            }}
          />
          <Route
            path={'/:eventHandle/hotel'}
            exact
            render={() => <LazyHotelBookingPartnerSearch />}
          />
          <Route
            path="/:eventHandle/car/search"
            render={({ match }) => <LazyGuestCarSearch eventHandle={match.params.eventHandle} />}
          />
          <Route
            path="/:eventHandle/car/details"
            render={({ match }) => <LazyGuestCarDetails eventHandle={match.params.eventHandle} />}
          />
          <Route
            path="/:eventHandle/hotel/search"
            render={({ match }) => <HotelSearchRoute eventHandle={match.params.eventHandle} />}
          />
          <Route
            path="/:eventHandle/hotel/:joyPlaceId?"
            render={({ match }) => (
              <LazyHotelDetails
                joyPlaceId={match.params.joyPlaceId}
                eventHandle={match.params.eventHandle}
              />
            )}
          />
          <Route
            path="/:eventHandle/hotelbooking"
            render={({ match }) => {
              const eventHandle = match.params.eventHandle;
              return <LazyHotelBookingApp eventHandle={eventHandle || null} />;
            }}
          />
          <Route
            path="/:eventHandle/book-your-stay"
            render={({ match }) => {
              const eventHandle = match.params.eventHandle;
              return <LazyBookYourStay eventHandle={eventHandle || null} />;
            }}
          />
          <Route
            path="/:eventHandle/albums"
            render={({ match }) => {
              const eventHandle = match.params.eventHandle;
              return (
                <>
                  <TelemetryProvider context={{ eventName: eventHandle }}>
                    <MediaCollection eventHandle={eventHandle || null} />
                  </TelemetryProvider>
                </>
              );
            }}
          />
          <Route
            path="/:eventHandle/collaborators"
            render={({ match }) => {
              const eventHandle = match.params.eventHandle;
              return (
                <TelemetryProvider context={{ eventName: eventHandle }}>
                  <LazyCollaborators eventHandle={eventHandle || null} />
                </TelemetryProvider>
              );
            }}
          />
          <Route
            path="/:eventHandle/cash-fund/:fundId"
            render={({ match, staticContext }) => {
              const eventHandle = match.params.eventHandle;
              const fundId = match.params.fundId;
              return (
                <TelemetryProvider context={{ eventName: eventHandle }}>
                  <LazyCashFundGuest
                    eventHandle={eventHandle || null}
                    fundId={fundId || null}
                    staticContext={staticContext}
                  />
                </TelemetryProvider>
              );
            }}
          />
          <Route
            path="/:eventHandle/tickets/:ticketId/pass"
            render={({ match }) => {
              const { eventHandle, ticketId } = match.params;
              return (
                <TelemetryProvider context={{ eventName: eventHandle }}>
                  <LazyTicketPass
                    eventHandle={eventHandle}
                    ticketId={ticketId}
                  />
                </TelemetryProvider>
              );
            }}
          />
          {/*
            Guest-facing registration form deep link. Registered BEFORE the page catch-all so the
            3-segment URL renders the form rather than being treated as a page slug ("registration").
            The bare /:eventHandle/registration (no formId) is intentionally NOT handled here — it is
            a host-built guest-site page and falls through to the catch-all below.
          */}
          <Route
            path="/:eventHandle/registration/:formId"
            render={({ match }) => {
              const { eventHandle, formId } = match.params;
              if (!eventHandle || !formId) return null;
              return (
                <TelemetryProvider context={{ eventName: eventHandle }}>
                  <LazyRegistrationGuestWithHead
                    eventHandle={eventHandle}
                    formId={formId}
                  />
                </TelemetryProvider>
              );
            }}
          />
          {/*
            Unified route handler for:
            1. Custom pages: /:eventHandle/page/:pageSlug (e.g., /myevent/page/custom-content)
            2. Standard pages (or whitelisted custom pages): /:eventHandle/:pageSlug (e.g., /myevent/travel)
            3. Root route: /:eventHandle (renders welcome page or first page by default)

            GuestSite component determines which page to render based on URL structure.
          */}
          <Route
            path={['/:eventHandle/page/:pageSlug', '/:eventHandle/:pageSlug', '/:eventHandle']}
            render={({ match, staticContext }) => {
              const eventHandle = match.params.eventHandle;
              return (
                <TelemetryProvider context={{ eventName: eventHandle }}>
                  <LazyGuestSite
                    eventHandle={eventHandle || null}
                    staticContext={staticContext}
                  />
                </TelemetryProvider>
              );
            }}
          />
        </Switch>
      </Box>
    </JoyKitThemeProvider>
  );
};

Guest.displayName = 'Guest';

export default Guest;
